import {useState, useEffect, useCallback, useRef} from 'react';
import {IFarm} from '../store/reducers/farm';
import {IOrganizationEntity, IPolygonEntity} from '@deep-planet/api-interfaces';
import {baseApiUrl} from '../config/const';
import axios from 'axios';

interface IVineHeartbeatResponse {
  polygonId: string;
  date: string;
  product: string;
  farmName: string;
  blockName: string;
  hectares: number;
  type: string;
  mean: string;
}

const MAX_LEGEND_POLYGON_NAME_LENGTH = 16;

export const useVineHeartbeat = (selectedOrganization: IOrganizationEntity, selectedFarm: IFarm, selectedPolygon: IPolygonEntity) => {
  const [traces, setTraces] = useState<Plotly.Data[]>([]);
  const [loadingHeartbeat, setLoadingHeartbeat] = useState<boolean>(true);
  const [product, setProduct] = useState<string>('NDVI');
  const prevUrlRef = useRef<string>(''); // Use ref instead of state to avoid re-renders

  const handleProductChange = (newProduct: string) => {
    setProduct(newProduct);
  };

  const getPlotValues = (data: IVineHeartbeatResponse[]) => {
    return {
      x: data.map(slice => slice.date),
      y: data.map(slice => slice.mean),
    };
  };

  const createTrace = (name: string, x: any[], y: any[]): Plotly.Data => {
    const shortName = name.length < MAX_LEGEND_POLYGON_NAME_LENGTH ? name : name.slice(0, MAX_LEGEND_POLYGON_NAME_LENGTH - 3) + '...';
    return {
      name: shortName,
      x,
      y,
      type: 'scatter',
      mode: 'lines',
      hovertemplate: name + '<extra></extra>',
    };
  };

  const filterData = useCallback((data: IVineHeartbeatResponse[]) => {
    const uniqueDates = [...new Set(data.map(d => d.date))];
    if (uniqueDates.length < 30) return data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);

    const filtered = data.filter(slice => new Date(slice.date) > fourWeeksAgo).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return new Set(filtered.map(d => d.date)).size > 1 ? filtered : [];
  }, []);

  const fetchData = useCallback(async (): Promise<IVineHeartbeatResponse[] | null> => {
    if (selectedFarm?.ownerOrganization.id !== selectedOrganization?.id) return null;

    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 1);
    fromDate.setDate(1);

    const toDate = new Date();
    toDate.setDate(toDate.getDate() - 7);

    const url = `${baseApiUrl}/dashboard/product/download-mean-data?organizationId=${selectedOrganization.id}&farmId=${
      selectedFarm.farmid
    }&fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}&product=${product}&vineHeartbeat=true`;

    if (url !== prevUrlRef.current) {
      try {
        const {data} = await axios.get(url);
        prevUrlRef.current = url;
        return data;
      } catch (err) {
        console.error('Error fetching heartbeat data:', err);
        setTraces([]);
      }
    }

    return null;
  }, [product, selectedFarm, selectedOrganization]);

  // Fetch for entire farm
  useEffect(() => {
    if (selectedFarm && selectedOrganization && selectedFarm.ownerOrganization.id === selectedOrganization.id && selectedFarm.polygons.length <= 10) {
      setLoadingHeartbeat(true);
      fetchData().then(data => {
        if (data) {
          const heartbeatData = filterData(data);
          const polygonIds = [...new Set(heartbeatData.map(d => d.polygonId))];
          const traces = polygonIds
            .map(polygonId => {
              const polygonData = heartbeatData.filter(d => d.polygonId === polygonId);
              const blockName = polygonData[0]?.blockName;
              const {x, y} = getPlotValues(polygonData);
              return x.length > 1 ? createTrace(blockName, x, y) : null;
            })
            .filter(Boolean);
          setTraces(traces);
        } else {
          setTraces([]);
        }
        setLoadingHeartbeat(false);
      });
    }
  }, [fetchData, filterData, selectedFarm, selectedOrganization]);

  // Fetch for selected polygon
  useEffect(() => {
    if (selectedFarm && selectedOrganization && selectedPolygon && selectedFarm.ownerOrganization.id === selectedOrganization.id && selectedFarm.polygons.length > 10) {
      setLoadingHeartbeat(true);
      fetchData().then(data => {
        if (data) {
          const heartbeatData = filterData(data);
          const polygonData = heartbeatData.filter(d => d.polygonId === selectedPolygon.id);
          const blockName = selectedFarm.polygons.find(p => p.id === selectedPolygon.id)?.name || '';
          const {x, y} = getPlotValues(polygonData);
          setTraces(x.length > 1 ? [createTrace(blockName, x, y)] : []);
        } else {
          setTraces([]);
        }
        setLoadingHeartbeat(false);
      });
    }
  }, [fetchData, filterData, selectedFarm, selectedPolygon, selectedOrganization]);

  return {product, handleProductChange, loadingHeartbeat, traces};
};
